import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import PropTypes from "prop-types";

import {fetchList} from "../../../../hook/axios.hook";
import {kpDocAdded} from "../../../../slices/KPSlice";
import COF_ASSET from "../../../../utilities/assets/COF";
import FunctionButton from "../../../../utilities/Buttons/FunctionButton";
import SwitchCustom from "../../../../utilities/Buttons/SwitchComponent/SwitchCustom";
import {SessionStorageManager} from "../../../../utilities/generalConstants.js/generalConstants";
import {toaster} from "../../../../utilities/hooks/toaster";
import LanguageSwitch from "../../../../utilities/LanguageSwitch/LanguageSwitch";
import LoaderWholePage from "../../../../utilities/Loader/LoaderWholePage";
import {moneyFormat} from "../../../../utilities/moneyFormat";
import SaveIndicator from "../../../../utilities/SaveIndicator/SaveIndicator";
import {UI_PERMISSION_TYPE} from "../../../adminForms/Ba3aSettings/tabs/PositionsSettings/useRoutePermissions";
import ModalForm from "../../../Modal/FormModal";
import {useModal} from "../../../Modal/ModalProvider";
import PdfFormatSwith from "../../../pdfGenerate/PdfFormatSwith";
import {deselectSku} from "../../../SKU/SkuSlice";
import TemplateCreateFromKpModal from "../../../Templates/TemplateRegistr/TemplateRegisterParts/TemplateCreateFromKpModal";
import ServicesAndDiscounts from "../kpFinForm/ServicesAndDiscounts/ServicesAndDiscounts";
import {
  addSkuPlanDoc,
  changeSequenceNumberV2,
  deselect,
  fetchAllCOfServices,
  fetchCOf,
  fetchSkuPlanListForKpf,
  getCurrentSkuPlan,
  getPlanSkuList,
  getSkuPlanInfoV2,
  selectCurrentCof,
  updateCOf,
} from "../KpFinSlice";

import useKpFinCollumnsService, {KP_FIN_TABLE_STORAGE_KEY} from "./utils/kpFinColumns";
import {generateCofPdf as makeCofPdf} from "./cofPdfGenerate";
import useKpFinDataService from "./KpFinDataService";
import useKpFinTableService from "./KpFinTableService";
import PdfPreviewModal from "./PdfPrevievModal";

import "./kpFinTable.scss";

export const pairedSkuPlanColumns = {
  image1: "image2",
  image2: "image1",
  discount: "summ",
  summ: "discount",
  topDiscount: "totalSumm",
  totalSumm: "topDiscount",
};

const KpFinTable = ({
  kpFin,
  isSaved,
  setIsSaved = () => {},
  isServicesAndDiscountsModalOpenedRef,
  tableOnly = false,
  forceEmptyData = false,
  onSettingsChange,
  initialSettings,
  persistColumnOrder = true,
  hasRejectedSkus,
  kpReadOnly = false,
}) => {
  const {
    t,
    i18n: {language},
  } = useTranslation();

  const dispatch = useDispatch();
  const {openModal} = useModal();

  const currentSkuPlan = useSelector(getCurrentSkuPlan);

  const currentCOf = useSelector(selectCurrentCof);
  const currentCOfSkuPlanIds = currentCOf?.skuPlan?.map(({skuPlanId}) => skuPlanId) ?? [];

  const updateCofData = () => {
    return dispatch(fetchCOf({cofId: currentCOf?.cofId})).unwrap();
  };

  useEffect(() => {
    if (forceEmptyData) return;
    if (currentCOf?.cofId) dispatch(fetchAllCOfServices({cofId: currentCOf?.cofId}));
  }, [currentCOf?.cofId, forceEmptyData]);

  const [pdfFormat, setPDFFormat] = useState("A4");
  const [pdfLanguage, setPdfLanguage] = useState(language);
  const [createTemplateModalActive, setCreateTemplateModalActive] = useState(false);

  const planSkuList = useSelector(getPlanSkuList);
  const planSkuListSkuPlanIds = planSkuList?.map(({skuPlanId}) => skuPlanId) ?? [];
  const hasSkuPlan = planSkuList?.length > 0;

  const hasChanges =
    currentCOfSkuPlanIds.sort((a, b) => a - b).join("") !== planSkuListSkuPlanIds.sort((a, b) => a - b).join("");

  const {getKpFinTableData} = useKpFinDataService();

  const kpFinTableData = useMemo(() => {
    if (forceEmptyData) return [];
    return [...(planSkuList ?? [])]?.sort((a, b) => a.sequenceNumber - b.sequenceNumber).map(getKpFinTableData) || [];
  }, [planSkuList, forceEmptyData]);

  // for numeration change, when user moves the lines in table
  const onReorder = useCallback(
    reorderedTableData => {
      if (reorderedTableData) {
        const sequenceObject = reorderedTableData.map(plan => {
          return {
            skuPlanId: plan.id,
            sequenceNumber: plan.number,
          };
        });

        // dispatch(changeSequenceNumber({sequenceObject}));
        dispatch(
          changeSequenceNumberV2({cofId: currentCOf?.cofId, body: sequenceObject, isRejected: !!hasRejectedSkus}),
        );
      }
    },
    [currentCOf],
  );

  const selectSkuPlan = id => {
    if (currentSkuPlan && currentSkuPlan.skuPlanId === id) {
      dispatch(deselect());
      return;
    }
    if (kpFin) {
      dispatch(getSkuPlanInfoV2(id));
      dispatch(deselectSku());
    }
  };

  const [areVerticalImgColumns, setAreVerticalImgColumns] = useState(false);

  const generateCofPdf = async () => {
    const services = await fetchList(`/cos/f/services/${currentCOf.cofId}`, "get");
    const currentCof = await updateCofData();

    if (!currentCOf) {
      console.error("no COf");
      return;
    }

    const COF = new COF_ASSET(currentCof, services);
    const header = await COF.formPdfHeader(pdfLanguage);

    const data = generateDataForExport(
      {
        text: [
          {text: "№:", bold: true},
          {text: `${currentCof.cofNumber || ""}`},
          {text: currentCof.cofSecondNumber ? ` (${currentCof.cofSecondNumber})` : ""},
        ],
      },
      {
        services,
        resultSumWithoutDiscount: moneyFormat(COF.totalSumWithoutDiscount),
        discountSum: moneyFormat(COF.discountSum),
        discount: COF.discountSum > 0,
        totalSumWithDiscount: moneyFormat(COF.totalSumWithDiscount),
        resultSum: moneyFormat(COF.sumPlan),
        resultAdvanceSum: moneyFormat(COF.skuPlanAdvance),
        advanceForServices: currentCof?.advanceForServices,
        hiddenRows,
      },
      header,
      pdfLanguage,
    );
    if (data.dataRows.length === 0) {
      toaster.error("KP can`t be empty");
      return null;
    }

    return await makeCofPdf({tableData: data, format: pdfFormat, pdfLanguage, areVerticalImgColumns});
  };

  const toggledColl = useMemo(
    () => [
      "article",
      "characteristics",
      "ovh",
      "image1",
      "image2",
      "complectDesc",
      // "price",
      "discount",
      "summ",
      "totalSumm",
      "topDiscount",
    ],
    [],
  );
  const [columns, setColumns] = useState([]);

  const handleColumnDragStart = (e, columnKey) => {
    e.dataTransfer.setData("text/plain", columnKey);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleColumnDrop = (e, columnKey) => {
    e.preventDefault();

    const sourceKey = e.dataTransfer.getData("text/plain");
    const pair = pairedSkuPlanColumns[sourceKey];

    setIsSaved(false);
    setColumns(prev => {
      const targetIndex = prev.findIndex(column => column.accessorKey === columnKey);
      const sourceCol = prev.find(column => column.accessorKey === sourceKey);
      const pairCol = pair && prev.find(column => column.accessorKey === pair);
      const filteredCols = prev.filter(column => {
        if (pair) {
          return column.accessorKey !== sourceKey && column.accessorKey !== pair;
        }
        return column.accessorKey !== sourceKey;
      });

      const newTargetIndex = filteredCols.slice(targetIndex)?.[0]?.notDraggable ? targetIndex - 1 : targetIndex;

      const newCols = [
        ...filteredCols.slice(0, newTargetIndex),
        sourceCol,
        ...(pair ? [pairCol] : []),
        ...filteredCols.slice(newTargetIndex),
      ];

      if (persistColumnOrder) {
        const kpFinTableStorageManager = new SessionStorageManager(KP_FIN_TABLE_STORAGE_KEY);
        kpFinTableStorageManager.setItem(newCols.map(col => col.accessorKey));
      }

      return newCols;
    });

    // setColumns(prev => {
    //   const newCols = prev.map(col => {
    //     const sourceCol = prev.find(column => column.accessorKey === sourceKey);
    //     const targetCol = prev.find(column => column.accessorKey === columnKey);
    //     if (col.accessorKey === sourceKey) return targetCol;
    //     if (col.accessorKey === columnKey) return sourceCol;
    //     return col;
    //   });

    //   if (persistColumnOrder) {
    //     const kpFinTableStorageManager = new SessionStorageManager(KP_FIN_TABLE_STORAGE_KEY);
    //     kpFinTableStorageManager.setItem(newCols.map(col => col.accessorKey));
    //   }

    //   return newCols;
    // });
  };

  const {collumns} = useKpFinCollumnsService(
    toggledColl,
    handleColumnDragStart,
    handleColumnDrop,
    initialSettings,
    kpReadOnly,
  );

  useEffect(() => {
    setColumns(collumns || []);
  }, [collumns]);

  const {KpFinTableRender, generateDataForExport, hiddenColumnsString, hiddenRows} = useKpFinTableService(
    columns,
    kpFinTableData,
    onReorder,
    selectSkuPlan,
    toggledColl,
    true,
    false,
    undefined,
    undefined,
    setIsSaved,
    forceEmptyData,
    false,
    false,
    false,
    setColumns,
    hasRejectedSkus,
    kpReadOnly,
  );

  const columnsPositionString = useMemo(
    () =>
      (columns || [])
        .map(({accessorKey}) => accessorKey)
        .filter(accessorKey => accessorKey !== "number")
        .join(","),
    [columns],
  );

  useEffect(() => {
    if (!onSettingsChange) return;

    const hiddenColumns = (hiddenColumnsString || "")
      .split(",")
      .filter(accessorKey => toggledColl.includes(accessorKey));

    const columnsPosition = (columns || [])
      .map(({accessorKey}) => accessorKey)
      .filter(accessorKey => accessorKey !== "number");

    onSettingsChange({hiddenColumns, columnsPosition});
  }, [onSettingsChange, hiddenColumnsString, columns, toggledColl]);

  const KpFinTable = KpFinTableRender();

  const [isPdfLoading, setPdfLoading] = useState(false);
  const pdfGenerationIdRef = useRef(0);

  if (tableOnly) {
    return KpFinTable;
  }

  const exportToPdf = async withDownload => {
    const generationId = ++pdfGenerationIdRef.current;

    setPdfLoading(true);
    try {
      const pdf = await generateCofPdf();
      if (generationId !== pdfGenerationIdRef.current) return;
      if (!pdf) {
        setPdfLoading(false);
        return;
      }

      if (withDownload) {
        openModal(
          <PdfPreviewModal
            pdfFile={pdf}
            exportToPdf={exportToPdf}
            linkBtnEnable
            fileName={currentCOf?.cofNumber}
            noBtns
          />,
          {label: `Pdf ${t("for COf №")} ${currentCOf?.cofNumber}`},
        );
      }

      // if (withDownload) pdf.download(`kpf_${currentCOf.cofNumber}_doc`);

      pdf.getBlob(file => {
        if (generationId !== pdfGenerationIdRef.current) return;

        const formData = new FormData();
        const fileName = `kpf_${currentCOf.cofNumber}_doc.pdf`;
        const columnsPosition = columnsPositionString.split(",").filter(Boolean);

        formData.append("doc", file, fileName);
        formData.append("hiddenColumns", hiddenColumnsString);
        formData.append("columnsPosition", columnsPositionString);

        dispatch(updateCOf({cofId: currentCOf?.cofId, body: {columnsPosition}, isRejected: !!hasRejectedSkus}))
          .unwrap()
          .then(() => dispatch(addSkuPlanDoc({cofId: currentCOf?.cofId, formData})).unwrap())
          .then(payload => {
            if (generationId !== pdfGenerationIdRef.current) return;

            dispatch(kpDocAdded(payload.docs));
            dispatch(fetchCOf({cofId: currentCOf?.cofId}));
            setIsSaved(true);
          })
          .catch(err => {
            console.error(err);
          })
          .finally(() => {
            if (generationId === pdfGenerationIdRef.current) setPdfLoading(false);
          });
      });
    } catch (err) {
      console.error(err);
      if (generationId === pdfGenerationIdRef.current) setPdfLoading(false);
    }
  };

  const onPreview = async () => {
    const generationId = ++pdfGenerationIdRef.current;
    const pdf = await generateCofPdf();
    if (generationId !== pdfGenerationIdRef.current) return;
    if (!pdf) return;
    openModal(
      <PdfPreviewModal pdfFile={pdf} exportToPdf={exportToPdf} linkBtnEnable fileName={currentCOf?.cofNumber} />,
      {label: t("Pdf preview and share")},
    );
  };

  const additionalCloseCondition = async currentCOf => {
    const cof = await dispatch(fetchCOf({cofId: currentCOf?.cofId}))
      .unwrap()
      .catch(err => console.error(err));

    return new Promise((resolve, reject) => {
      !cof?.isChanged && cof?.advanceForServices === currentCOf?.advanceForServices ? resolve() : reject();
    });
  };

  const onServicesAndDiscountsButtonClick = () => {
    if (!currentCOf) return;

    isServicesAndDiscountsModalOpenedRef.current = true;
    openModal(<ServicesAndDiscounts />, {
      label: `${t("Services and discounts CO(f) №")} ${currentCOf.cofNumber}`,
      setIsSaved: setIsSaved,
      onSave: () => {
        additionalCloseCondition(currentCOf).catch(() => exportToPdf(false));
      },
      onModalClose: () => {
        dispatch(fetchSkuPlanListForKpf(currentCOf.cofId));
        isServicesAndDiscountsModalOpenedRef.current = false;

        // updateCofData();
      },
      additionalCloseCondition: () => additionalCloseCondition(currentCOf),
    });
  };

  return (
    <>
      {!kpReadOnly && (
        <div className="relative row w100 pb0_5">
          <SaveIndicator
            isSaved={isSaved || !hasSkuPlan}
            className="pa"
            style={{top: "-1.9rem", left: "5%", zIndex: 500}}
            onClick={() => exportToPdf(false)}
          />
        </div>
      )}

      {KpFinTable}
      {!kpReadOnly && (
        <div
          className="fc jcsb"
          style={{
            marginBottom: "10px",
            bottom: "-0.23em",
            width: "100%",
          }}
        >
          <div className="fc">
            <FunctionButton
              name={t("Save + pdf")}
              onClick={() => exportToPdf(true)}
              disabled={planSkuList?.length === 0}
              tooltipMessage={isSaved ? "Saved" : ""}
              style={{marginLeft: "10px"}}
            />
            <div className="fc" style={{gap: "15px", padding: "0 10px"}}>
              <PdfFormatSwith setFormat={setPDFFormat} />
              <LanguageSwitch setLanguage={setPdfLanguage} />
            </div>
            <FunctionButton
              name={t("PDF preview")}
              style={{marginLeft: "10px"}}
              onClick={onPreview}
              disabled={planSkuList?.length === 0}
            />
          </div>
          <SwitchCustom
            leftLabel="Image columns 2in1"
            tooltipMessage="Vertical image columns"
            rightLabel=""
            checked={areVerticalImgColumns}
            onChange={e => {
              setAreVerticalImgColumns(e.target.checked);
            }}
            scale={0.7}
            invertedActiveLabel
            className="pt5"
          />
          <FunctionButton
            name={t("Services and discounts")}
            style={{marginLeft: "10px"}}
            disabled={
              hasRejectedSkus ||
              !isSaved ||
              hasChanges ||
              planSkuList?.length === 0 ||
              currentCOf?.skuPlan?.length === 0
            }
            onClick={onServicesAndDiscountsButtonClick}
            tooltipMessage={
              hasChanges || !isSaved
                ? "COf has to be saved after being changed"
                : planSkuList?.length === 0 || currentCOf?.skuPlan?.length === 0
                ? "Add sku to COf and save changes first"
                : ""
            }
          />
          <div className="fc">
            <FunctionButton
              name={t("Template")}
              style={{marginLeft: "10px"}}
              onClick={() => {
                setCreateTemplateModalActive(true);
              }}
              tooltipMessage={t("Template can be created only after saving actual changes")}
              disabled={
                hasRejectedSkus ||
                !isSaved ||
                hasChanges ||
                planSkuList?.length === 0 ||
                currentCOf?.skuPlan?.length === 0
              }
              autoWidth
            />
            <FunctionButton name={t("Dublicate")} style={{marginLeft: "10px"}} disabled={true} noFunctional autoWidth />
            <FunctionButton name={t("Postings")} style={{marginLeft: "10px"}} disabled={true} noFunctional autoWidth />
          </div>
        </div>
      )}
      {kpReadOnly && (
        <div
          className="fc aic jcfe gap5x"
          style={{
            marginBottom: "10px",
            bottom: "-0.23em",
            width: "100%",
          }}
        >
          <FunctionButton
            name={t("Services and discounts")}
            style={{marginLeft: "10px"}}
            onClick={() => {
              if (!currentCOf) return;
              openModal(<ServicesAndDiscounts />, {
                label: `${t("Services and discounts CO(f) №")} ${currentCOf.cofNumber}`,
                closable: true,
                onModalClose: () => {},
                onSave: () => {},
                kpReadOnly,
              });
            }}
            autoWidth
            uiPermissionType={UI_PERMISSION_TYPE.READABLE}
          />
        </div>
      )}
      <ModalForm
        Component={TemplateCreateFromKpModal}
        label={t("Creating template tCO")}
        active={createTemplateModalActive}
        setModalActive={setCreateTemplateModalActive}
        className="largeModal"
        currentCOf={currentCOf}
        onModalClose={updateCofData}
      />
      {isPdfLoading && <LoaderWholePage />}
    </>
  );
};

KpFinTable.propTypes = {
  onUpdate: PropTypes.func,
  currentCOf: PropTypes.object,
  setCurrSkuPlan: PropTypes.func,
  active: PropTypes.object,
  setActive: PropTypes.func,
  tableOnly: PropTypes.bool,
  forceEmptyData: PropTypes.bool,
  persistColumnOrder: PropTypes.bool,
  onSettingsChange: PropTypes.func,
  initialSettings: PropTypes.shape({
    hiddenColumns: PropTypes.arrayOf(PropTypes.string),
    columnsPosition: PropTypes.arrayOf(PropTypes.string),
  }),
};

export default KpFinTable;
